import React from "react";
import { ButtonBase } from "@/components/ui/button";
import { ButtonProps, ButtonSize, ButtonVariant } from "./Button.types";
import { cn } from "@/utils/cn";

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = "primary",
      size = "md",
      isLoading = false,
      leftIcon,
      rightIcon,
      neumorph,
      className = "",
      disabled = false,
      onClick,
      children,
      fullWidth = false,
      testId,
      asChild,
      type = "button",
      ...props
    },
    ref
  ) => {

    const baseStyles = "inline-flex items-center justify-center font-medium rounded transition-all duration-200";

    const sizeStyles: Record<ButtonSize, string> = {
      xs: "min-w-[50px] px-2 py-1 text-xs",
      sm: "min-w-[80px] px-3 py-1.5 text-sm",
      md: "min-w-[110px] px-4 py-2 text-base",
      lg: "min-w-[150px] px-6 py-3 text-lg",
    };

    const variantStyles: Record<ButtonVariant, string> = {
      primary: "bg-primary-500 hover:bg-primary-600 active:bg-primary-700 text-white",
      secondary: "bg-secondary-500 hover:bg-secondary-600 active:bg-secondary-700 text-white",
      success: "bg-green-500 hover:bg-green-600 active:bg-green-700 text-white",
      danger: "bg-red-500 hover:bg-red-600 active:bg-red-700 text-white",
      outline: "border border-primary-500 text-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900",
      ghost: "text-primary-500 hover:bg-primary-50 dark:hover:bg-primary-900",
      link: "text-primary-500 underline hover:text-primary-600 dark:hover:text-primary-300",
      neutral: "bg-gray-500 hover:bg-gray-600 active:bg-gray-700 text-white",
      excel: "bg-[#1D6F42] hover:bg-[#198442] active:bg-[#106B35] text-white",
    };

    const buttonClass = cn(
      baseStyles,
      sizeStyles[size],
      variantStyles[variant],
      fullWidth && "w-full",
      className
    );

    return (
      <ButtonBase
        ref={ref}
        className={buttonClass}
        disabled={disabled || isLoading}
        onClick={onClick}
        data-testid={testId}
        asChild={asChild}
        type={type}
        {...props}
      >
        {isLoading ? (
          <svg className="animate-spin h-5 w-5 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
        ) : (
          <>
            {leftIcon && <span className="mr-2">{leftIcon}</span>}
            {children}
            {rightIcon && <span className="ml-2">{rightIcon}</span>}
          </>
        )}
      </ButtonBase>
    );
  }
);
Button.displayName = "Button";
